/** Public pages with forms or legal text: post a requirement, check a requirement, contact, certificate check, terms, privacy. */
import { api } from '../../core/api.js';
import { config } from '../../core/config.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDate } from '../../core/format.js';
import { navigate } from '../../core/router.js';
import { update } from '../../app/shell.js';
import { backLink, banner, ic, kv } from '../../ui/components.js';
import { field } from '../../ui/forms.js';
import { submitForm } from '../../ui/submit.js';
import { requirementBody, requirementFields } from '../requirements/form.js';
import { pubHead } from './blocks.js';

const OPEN = { auth: false };
const onError = (error) => update((ui) => { ui.error = error.message; });

/* ---------- post a requirement (no account) ---------- */

const requirements = {
  path: '/requirements',
  title: 'Post a requirement',
  ui: () => ({ error: '', done: null }),
  view(_, ui) {
    if (ui.done) {
      return html`<div class="sec" style="max-width:640px"><div class="card empty">${ic('check')}<h3>We have your requirement</h3>
        <p>Your reference is <b class="mono">${ui.done}</b>. Keep it to check the status later. Our team reviews every requirement before any profile is shared.</p>
        <div class="row" style="justify-content:center"><a class="btn" href="/requirements/status?ref=${encodeURIComponent(ui.done)}">Check status</a><a class="btn ghost" href="/">Back to home</a></div></div></div>`;
    }
    return html`<div class="sec" style="max-width:820px">${pubHead('Tell us what you need', 'No account needed. We review it, match prepared people, and share a short list.')}
      <div class="card"><form data-submit="requirements.submit" novalidate>
        ${requirementFields({ prefix: 'pr', contact: true })}
        ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}
        <p class="xs mu">We use your details only to respond to this requirement. See the <a href="/privacy">privacy notice</a>.</p>
        <button class="btn lg mt-s" type="submit">Send requirement</button>
      </form></div></div>`;
  },
};

register({
  'requirements.submit': (form) => submitForm(
    form,
    async (values) => {
      const body = await api.post('/public/requirements', { ...OPEN, body: requirementBody(values) });
      update((ui) => { ui.done = body.reference; ui.error = ''; });
      window.scrollTo(0, 0);
    },
    { onError },
  ),
});

const requirementStatus = {
  path: '/requirements/status',
  title: 'Requirement status',
  ui: (ctx) => ({ error: '', result: null, ref: ctx.query?.get('ref') || '' }),
  async load(ctx) {
    const ref = ctx.query.get('ref');
    if (!ref) return { result: null };
    try {
      return { result: await api.get(`/public/requirements/${encodeURIComponent(ref)}`, { ...OPEN, signal: ctx.signal }) };
    } catch (error) {
      if (error.status === 404) return { result: null, notFound: ref };
      throw error;
    }
  },
  view(d, ui) {
    return html`<div class="sec" style="max-width:560px">${backLink('/requirements', 'Post a requirement')}
      <div class="card"><h2>Check a requirement</h2>
        <form data-submit="requirements.lookup" novalidate>${field('st-ref', 'Reference', { name: 'ref', ph: 'REQ-AB12CD', val: ui.ref, err: 'Enter your reference.' })}<button class="btn" type="submit">Check</button></form>
        ${d.notFound ? banner(`No requirement found for ${d.notFound}. Check the reference and try again.`, 'bad', 'alert') : ''}
        ${d.result ? html`<div class="mt">${kv([['Reference', d.result.reference], ['Title', d.result.title], ['Status', d.result.status]])}</div>` : ''}</div></div>`;
  },
};

register({
  'requirements.lookup': (form) => submitForm(form, async ({ ref }) => {
    await navigate(`/requirements/status?ref=${encodeURIComponent(ref.toUpperCase())}`);
  }),
});

/* ---------- contact ---------- */

const contact = {
  path: '/contact',
  title: 'Contact',
  ui: () => ({ error: '', sent: false }),
  view(_, ui) {
    return html`<div class="sec" style="max-width:640px">${pubHead('Contact us', `Write to the ${config.companyName} team. We reply within two working days.`)}
      <div class="card">${ui.sent
        ? html`<div class="empty">${ic('check')}<h3>Message sent</h3><p>Thank you. We will reply by email.</p></div>`
        : html`<form data-submit="contact.send" novalidate>
          <div class="fgrid">${field('ct-name', 'Your name', { name: 'name', minlen: 2, autocomplete: 'name' })}${field('ct-email', 'Email', { type: 'email', name: 'email', autocomplete: 'email' })}</div>
          ${field('ct-msg', 'How can we help?', { name: 'message', rows: 5, minlen: 10, max: 3000, err: 'Write at least a sentence.' })}
          ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}
          <button class="btn lg" type="submit">Send message</button></form>`}
      </div><p class="sm mu mt-s">Prefer email? <a href="mailto:${config.supportEmail}">${config.supportEmail}</a></p></div>`;
  },
};

register({
  'contact.send': (form) => submitForm(
    form,
    async (values) => {
      await api.post('/public/contact', { ...OPEN, body: values });
      update((ui) => { ui.sent = true; ui.error = ''; });
    },
    { onError },
  ),
});

/* ---------- certificate check ---------- */

const verifyCertificate = {
  path: '/verify/:code',
  title: 'Certificate check',
  async load(ctx) {
    try {
      return { cert: await api.get(`/public/certificates/${encodeURIComponent(ctx.params.code)}`, { ...OPEN, signal: ctx.signal }) };
    } catch (error) {
      if (error.status === 404) return { cert: null };
      throw error;
    }
  },
  view(d, _ui, ctx) {
    const c = d.cert;
    return html`<div class="sec" style="max-width:640px"><div class="card">${c
      ? html`<div class="row" style="gap:10px">${ic('check')}<h2 style="margin:0">Valid certificate</h2></div>
        <div class="mt">${kv([['Holder', c.holder], ['Course', c.course], ['Score', `${c.score_percent}%`], ['Trainer', c.trainer], ['Issued', fmtDate(c.issued_at)], ['Code', html`<span class="mono">${c.code}</span>`]])}</div>
        <p class="xs mu mt-s">Issued by ${config.companyName}. Only the holder's name and course are shown here.</p>`
      : html`<div class="empty">${ic('alert')}<h3>No certificate found</h3><p>There is no certificate with the code <span class="mono">${ctx.params.code}</span>. Check the code and try again.</p></div>`}
    </div></div>`;
  },
};

/* ---------- legal ---------- */

const UPDATED = '15 October 2026';

function legal(title, sections) {
  return html`<div class="sec" style="max-width:760px"><h1 style="font-size:34px">${title}</h1><p class="mu">Last updated ${UPDATED}. Issued by ${config.companyName}.</p>
    ${sections.map(([heading, text]) => html`<h3 class="mt">${heading}</h3>${[].concat(text).map((p) => html`<p>${p}</p>`)}`)}
    <p class="xs mu mt">Questions: <a href="mailto:${config.supportEmail}">${config.supportEmail}</a></p></div>`;
}

// NOTE: this wording is a starting point for review by the company's lawyer. It is not legal advice and
// must be approved (and the CONSENT_VERSION on the backend bumped) before real users sign up.
const terms = {
  path: '/terms',
  title: 'Terms of use',
  view: () => legal('Terms of use', [
    ['Using the service', `${config.brandName} connects students, working professionals, companies and startups through live classes, recordings and moderated messaging. You must give true information and keep your login private.`],
    ['Classes and payments', 'Course fees are shown before you pay, with GST added where applicable. Access lasts for the period you paid for. Refunds follow the policy shown at checkout.'],
    ['Freelancers and deposit', 'Freelancers who take classes pay a refundable deposit. The conditions for refund, and the period for which it is held, are shown when you pay it.'],
    ['Conduct', 'No spam, harassment, fake profiles, or asking others for money. Messages are moderated and may be held for review. We may suspend accounts that break these rules.'],
    ['Recordings and content', 'Recorded classes are for the enrolled student only, for the period shown. Do not copy or share them. Trainers keep the rights to their own material.'],
    ['Liability', `${config.brandName} does not guarantee a job, project or income. Companies and freelancers agree their own terms of work.`],
    ['Contact and disputes', 'Write to us first; we will try to resolve it. Indian law applies.'],
  ]),
};

const privacy = {
  path: '/privacy',
  title: 'Privacy notice',
  view: () => legal('Privacy notice', [
    ['What we collect', 'Your name, email, phone number, and what you add to your profile; class attendance and progress; messages you send on the platform; payment records from our payment partner; and basic technical data such as IP address for security.'],
    ['Why we use it', 'To run your account, classes and payments; to match people with opportunities; to keep the platform safe; and to meet legal duties such as tax records.'],
    ['Who sees it', 'Companies see a candidate only after an administrator approves the match and the candidate is shortlisted; contact details are hidden until then. We share data with service providers who run payments, SMS, video and storage for us.'],
    ['Your rights', 'You can download your data and delete your account from the Account page. Invoices and orders are kept as the law requires.'],
    ['Keeping it safe', 'Access to classes and recordings uses short-lived signed links. Passwords are stored hashed.'],
    ['Contact', 'Write to the address below for any privacy request or complaint.'],
  ]),
};

export const routes = [requirements, requirementStatus, contact, verifyCertificate, terms, privacy];
